import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useAppSumoSignIn } from 'Hooks/Auth';
import { AuthStatuses } from 'Interfaces/Auth';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { User } from 'Interfaces/User';
import { DataLayerAnalytics } from 'Services/Integrations';
import Toast from 'Services/Toast';
import { applyAppSumoLink } from 'Store/ducks/user/actionCreators';
import { selectIsAppSumoLinkUsed, selectUser } from 'Utils/selectors';

import AppSumoThanks from './AppSumoThanks';

const LoadingUserScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const user: User = useSelector(selectUser);
  const isAppSumoLinkUsed = useSelector(selectIsAppSumoLinkUsed);

  const [signIn, isLoading] = useAppSumoSignIn();

  const appSumoToken = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    return searchParams.get('appSumoToken');
  }, [location.search]);

  const navigateToRoot = useCallback(() => {
    navigate(AuthorizedRoutePaths.BASE_PATH, { replace: true });
  }, [navigate]);

  const handleAppSumoSignIn = useCallback(async () => {
    try {
      await signIn({ token: appSumoToken as string });
      DataLayerAnalytics.fireAppSumoRegistrationEvent();
    } catch (error) {
      Toast.handleErrors(error);
      navigateToRoot();
    }
  }, [appSumoToken, navigateToRoot, signIn]);

  useEffect(() => {
    if (!appSumoToken) {
      return navigateToRoot();
    }

    if (!isAppSumoLinkUsed) {
      dispatch(applyAppSumoLink());
      handleAppSumoSignIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateToRoot, appSumoToken]);

  if (!user.authStatus || user.authStatus === AuthStatuses.UNAUTHORIZED || isLoading) {
    return <UISpinner width={50} height={50} className="spinner--main__wrapper" />;
  }

  return <AppSumoThanks />;
};

export default LoadingUserScreen;
